import {
  FC, useEffect, useRef, useState,
} from 'react';
import StreamingAvatar, { AvatarQuality, StreamingEvents } from '@heygen/streaming-avatar';
import {
  message, Button, Typography, Slider, Row,
} from 'taxaroo-ui';
import VolumeIcon from 'src/assets/icons/volume.svg';
import ExpandIcon from 'src/assets/icons/expand.svg';
import CollapseIcon from 'src/assets/icons/collapse.svg';
import WorkforceIcon from 'src/assets/icons/workforce.svg';
import { isMobileView } from '~src/components/helpers/screen';
import Mic from 'src/assets/icons/mic.svg';
import * as styles from './styles.module.css';
import { FirmAiPlanType, TaxarooAIAvatarPoster } from './utils';

const { Text, Link } = Typography;

export type HeygenAvatarProps = {
  token: string;
  avatarId: string;
  voiceId: string;
  secondsLeft: number;
};

interface AvatarProps extends HeygenAvatarProps {
  setSteamingAvatar: (val: StreamingAvatar, sessionId: string) => void;
  heygenStreamStart: () => void;
  setFullscreenAvatar: (isFullScreen: boolean) => void;
  isTrialUsageLimitExceeded: boolean;
  isHeygenUsageLimitExceeded: boolean;
  isHeygenLoading: boolean;
  planType: FirmAiPlanType;
  isRecording: boolean;
  isRecordingLoading: boolean;
  toggleRecording: () => Promise<void>;
}

const HeygenAvatar:FC<AvatarProps> = ({
  token,
  avatarId,
  voiceId,
  secondsLeft,
  setSteamingAvatar,
  heygenStreamStart,
  setFullscreenAvatar,
  isTrialUsageLimitExceeded,
  isHeygenUsageLimitExceeded,
  isHeygenLoading,
  planType,
  isRecording,
  isRecordingLoading,
  toggleRecording,
}) => {
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [stream, setStream] = useState<MediaStream>();
  const [volume, setVolume] = useState(100);
  const [secondsRemaining, setSecondsRemaining] = useState(secondsLeft);
  const videoElementRef = useRef<HTMLVideoElement>();
  const avatar = useRef<StreamingAvatar>();
  let countDownInt = 0;

  const secondsToTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const startSession = async () => {
    try {
      setIsLoadingSession(true);
      avatar.current = new StreamingAvatar({ token });
      const { session_id: sessionId } = await avatar.current.createStartAvatar({
        quality: AvatarQuality.Medium,
        avatarName: avatarId,
        voice: {
          voiceId,
        },
        language: 'en',
        // video_encoding: 'H264',
      });

      setIsLoadingSession(false);

      avatar.current?.on(StreamingEvents.STREAM_READY, (event) => {
        setStream(event.detail);
        videoElementRef.current?.classList.remove(styles.blured);
        setSteamingAvatar(avatar.current, sessionId);
        clearInterval(countDownInt);
        countDownInt = window.setInterval(() => setSecondsRemaining((sec) => sec - 1), 1000);
      });

      avatar.current?.on(StreamingEvents.STREAM_DISCONNECTED, () => {
        videoElementRef.current?.classList.add(styles.blured);
        setStream(undefined);
        avatar.current = null;
        clearInterval(countDownInt);
      });
    } catch (error) {
      setIsLoadingSession(false);
      message.error(error.message, 10);
    }
  };

  useEffect(() => {
    if (token) {
      startSession();
    } else {
      clearInterval(countDownInt);
    }

    return () => {
      avatar.current = null;
      setStream(undefined);
    };
  }, [token]);

  useEffect(() => {
    if (stream && videoElementRef.current) {
      videoElementRef.current.srcObject = stream;
      videoElementRef.current.onloadedmetadata = () => {
        videoElementRef.current!.play();
      };
    }
  }, [videoElementRef, stream]);

  useEffect(() => {
    setSecondsRemaining(secondsLeft);
  }, [secondsLeft]);

  return (
    <>
      <video
        className={isMobileView() ? styles.videoContainerMobile : styles.videoContainer}
        ref={videoElementRef}
        playsInline
        poster={TaxarooAIAvatarPoster}
        onError={(err) => message.err(err)}
      >
        <track kind="captions" label="English" default />
      </video>

      {stream && (
        <>
          <Row className={[styles.avatarBtn, styles.volumeControl]} justify="center">
            <VolumeIcon className={styles.volumeControlIcon} />
            <Slider
              className={styles.volumeControlSlider}
              vertical
              step={10}
              value={volume}
              onChange={(val: number) => {
                setVolume(val);
                if (videoElementRef.current) videoElementRef.current.volume = val / 100;
              }}
              handleStyle={{
                width: '11px', height: '11px', backgroundColor: '#fff', border: '1px solid #fff',
              }}
              trackStyle={{ background: '#fff', width: '1px' }}
              railStyle={{ background: '#999', width: '1px' }}
            />
          </Row>

          <Button
            type="default"
            shape="round"
            onClick={() => avatar.current?.interrupt()}
            className={[styles.avatarBtn, styles.interruptBtn]}
          >
            <Text className={styles.interruptBtnTxt}>Interrupt</Text>
          </Button>

          <Button
            shape="circle"
            className={
              isRecording
                ? [styles.micBtnAvatar, styles.avatarBtn, styles.avatarMicBtnRed]
                : [styles.micBtnAvatar, styles.avatarBtn, styles.avatarMicBtn]
            }
            loading={isRecordingLoading}
            onClick={() => toggleRecording()}
            disabled={isTrialUsageLimitExceeded}
          >
            {!isRecordingLoading && (<Mic className={isRecording ? styles.micRecAvatar : styles.micDefAvatar} />)}
          </Button>

          <Row className={[styles.avatarBtn, styles.countDown]}>
            {secondsToTime(secondsRemaining)}
          </Row>
        </>
      )}

      {!stream && !isTrialUsageLimitExceeded && !isHeygenUsageLimitExceeded && !isLoadingSession && !isHeygenLoading && (
        <>
          <Row className={styles.avatarTopText}>
            <Text className={styles.whiteText}>Taxaroo AI can hear and talk</Text>
          </Row>

          {planType === 'trial' && (
            <Row className={styles.avatarBeforeBtnText}>
              <Text className={styles.whiteText}>
                Ask him tax questions, get help with operations and have him do tasks for you
              </Text>
            </Row>
          )}

          <Button
            type="primary"
            shape="round"
            onClick={() => heygenStreamStart()}
            className={planType === 'trial' ? styles.avatarStartConversationBtn : styles.avatarStartConversationBtnCenter}
          >
            Start Conversation
          </Button>

          {planType === 'trial' && (
            <Row className={styles.avatarAfterBtnText}>
              You have 5 minutes free
            </Row>
          )}
        </>
      )}

      {(isLoadingSession || isHeygenLoading) && (
        <>
          <Row className={styles.avatarTopText}>
            <Text className={styles.whiteText}>Taxaroo AI can hear and talk</Text>
          </Row>
          <Row className={styles.avatarTextCenter} justify="center">
            <Text className={styles.whiteText}>Connecting</Text>
            <Text className={styles.connectingText}>&nbsp;</Text>
          </Row>
        </>
      )}

      {isHeygenUsageLimitExceeded && (
        <>
          <Row className={styles.avatarTopText}>
            <Text className={styles.whiteText}>Taxaroo AI can hear and talk</Text>
          </Row>
          <Row className={styles.avatarTextCenter}>
            <Text className={styles.whiteText}>
              Hey you&apos;ve hit the plan limits.
              To manage your subscription click&nbsp;
              <Link href="/settings/ai">here</Link>
            </Text>
          </Row>
        </>
      )}

      {isTrialUsageLimitExceeded && (
        <div className={styles.avatarTrilaText}>
          <Row style={{ paddingBottom: '17px', fontSize: '17px' }}>
            Unlock the Full Power of Taxaroo AI
          </Row>
          <ul>
            <li style={{ paddingBottom: '17px' }}>
              Talk to your Agent like you talk to your friends.
            </li>
            <li style={{ paddingBottom: '17px' }}>
              Complete tax forms, send emails, and tackle administrative tasks in seconds, freeing up hours of your day.
            </li>
            <li style={{ paddingBottom: '17px' }}>
              Expert-level insights and ensure every tax-related decision aligns with current regulations.
            </li>
            <li>
              Your AI tax assistant is available whenever you need help—no waiting, no appointments.
            </li>
          </ul>
        </div>
      )}

      {isMobileView() && stream && (
        <Button
          type="default"
          shape="circle"
          onClick={() => { setFullscreenAvatar(!isFullScreen); setIsFullScreen(!isFullScreen); }}
          className={[styles.avatarBtn, styles.expandBtn]}
        >
          {isFullScreen
            ? <CollapseIcon className={styles.collapseBtnIcon} />
            : <ExpandIcon className={styles.expandBtnIcon} />}
        </Button>
      )}

      <Row className={stream ? styles.poweredBy : styles.poweredByWhite}>
        Powered by &nbsp;&nbsp;
        <WorkforceIcon className={stream ? styles.poweredByIcon : styles.poweredByIconWhite} />
        &nbsp;
        <Text className={stream ? styles.poweredByCompanyName : styles.poweredByCompanyNameWhite}>Workforce AI</Text>
      </Row>
    </>
  );
};

export default HeygenAvatar;
